// Typed wrappers for the Star Forge HTTP API.
//
// Field names mirror the Rust serde output exactly (snake_case from
// `ui/src/spend_aggregator.rs::SpendSummary`). Do NOT camelCase these — the
// backend serializes with the struct's field names verbatim and VendorClass
// uses `#[serde(rename_all = "snake_case")]` to produce 'sponsorship' / 'cash'.

export type SpendWindow = '24h' | '7d' | '30d' | 'total' | 'since_start'

export type VendorClass = 'sponsorship' | 'cash'

export interface VendorSpend {
  spend: number
  request_count: number
}

export interface ModelSpend {
  model: string
  vendor: VendorClass
  spend: number
  request_count: number
}

export interface SpendSummary {
  project: string
  window: string
  sponsorship_credit: VendorSpend
  cash: VendorSpend
  request_count: number
  by_model: ModelSpend[]
  last_updated: string // ISO-8601
  // Per-run cost figure. Optional so an older backend's reply still renders.
  /** The request tag matched, `project=<workflow project name>`. */
  tag?: string
  /** The bounds summed over, both inclusive (ISO-8601). */
  range_start?: string
  range_end?: string
  /** window | request | run_chain_start | current_run_start | unknown_run_start */
  start_source?: string
  /** False when LiteLLM held more rows than one fetch reads: the total is a lower bound. */
  complete?: boolean
}

export async function fetchProjectSpend(
  projectName: string,
  window: SpendWindow,
  refresh = false,
  getAuthHeaders: () => Record<string, string>,
): Promise<SpendSummary> {
  const url = `/api/projects/${encodeURIComponent(projectName)}/spend?window=${window}${refresh ? '&refresh=true' : ''}`
  const resp = await fetch(url, { headers: getAuthHeaders() })
  if (!resp.ok) throw new Error(`spend fetch failed: ${resp.status}`)
  return resp.json()
}

// A full-response agent log on a git ref. Cloud agents push these per-loop
// responses to `.starforge/logs/live/<persona>-<worker>-<task>.log`; the
// activity feed only has one-liners, so these are read on drill-down.
export interface LiveLogRef {
  git_ref: string
  path: string
  name: string
}

/** List discoverable live logs across main + task branches (drill-down only). */
export async function listLiveLogs(
  getAuthHeaders: () => Record<string, string>,
): Promise<LiveLogRef[]> {
  const resp = await fetch('/api/projects/livelogs', { headers: getAuthHeaders() })
  if (!resp.ok) throw new Error(`live-log list failed: ${resp.status}`)
  return resp.json()
}

/** Read the full content of one live log from a git ref (drill-down only). */
export async function fetchLiveLog(
  path: string,
  gitRef: string | undefined,
  getAuthHeaders: () => Record<string, string>,
): Promise<string> {
  const params = new URLSearchParams({ path })
  if (gitRef) params.set('ref', gitRef)
  const resp = await fetch(`/api/projects/livelog?${params.toString()}`, { headers: getAuthHeaders() })
  if (!resp.ok) throw new Error(`live-log fetch failed: ${resp.status}`)
  return resp.text()
}

/**
 * Find the live-log ref for a persona/worker/task triple by matching against the
 * `<persona>-<worker>-<task>.log` naming convention. Case-insensitive on persona
 * (activity.log capitalizes; filenames are lowercase). Returns the best match or
 * null. Matching is done client-side against the cheap `listLiveLogs` result so
 * we only pull full content for the one the user drilled into.
 */
export function matchLiveLog(
  refs: LiveLogRef[],
  opts: { persona?: string; worker?: string; taskId?: string },
): LiveLogRef | null {
  const persona = opts.persona?.toLowerCase()
  const worker = opts.worker?.toLowerCase()
  const taskId = opts.taskId?.toUpperCase()

  const scored = refs
    .map(ref => {
      const base = ref.name.replace(/\.log$/i, '').toLowerCase()
      let score = 0
      if (persona && base.startsWith(`${persona}-`)) score += 4
      if (worker && base.includes(`-${worker}-`)) score += 2
      if (taskId && base.toUpperCase().endsWith(`-${taskId}`)) score += 3
      return { ref, score }
    })
    .filter(s => s.score > 0)
    .sort((a, b) => b.score - a.score)

  return scored.length > 0 ? scored[0]!.ref : null
}
